import { useEffect, useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import api from '@/lib/api'
import { ExternalLink, ShieldCheck } from 'lucide-react'

/**
 * Account settings.
 *
 * Credentials and two-factor live at 42QLabs, so this page no longer changes passwords or
 * enrols TOTP factors — it shows who you are here and points at the place that owns the
 * rest. The alternative, keeping the forms and having them fail, would just move the
 * discovery of "Plutus does not manage this any more" to the moment someone needs it.
 */

export default function UserProfile() {
  const user = useAuthStore((s) => s.user)
  const [issuer, setIssuer] = useState<string | null>(null)

  useEffect(() => {
    api.get('/auth/status')
      .then(r => setIssuer(r.data?.central?.issuer ?? null))
      .catch(() => setIssuer(null))
  }, [])

  // The issuer is `<host>/application/o/plutus/`; the account page is the host's own
  // user interface. Derived rather than configured, so it follows the issuer if it moves.
  let accountUrl: string | null = null
  try { if (issuer) accountUrl = new URL(issuer).origin + '/if/user/' } catch { /* not a URL */ }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold">Profile</h1>
        <p className="text-muted-foreground">Your account in Plutus</p>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Account Information</h2>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Email</span>
            <span className="text-sm font-medium">{user?.email}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Name</span>
            <span className="text-sm font-medium">{user?.full_name || '-'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Role</span>
            <span className={`px-2 py-1 rounded text-xs ${
              user?.role === 'Admin' ? 'bg-primary/10 text-primary' : 'bg-secondary'
            }`}>
              {user?.role}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Security</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your password and two-factor authentication are managed by your 42QLabs
              account, not by Plutus. Changes there apply to every 42QLabs application.
            </p>
            {accountUrl && (
              <a
                href={accountUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                Manage your 42QLabs account
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
